import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import api from '../../utils/api';
import AdminHeader from '../../components/AdminHeader';
import { ORDER_STATUS_OPTIONS, getStatusConfig } from '../../utils/orderStatusConstants';
import './AdminOrders.css';

const ALLOWED_STATUS_FILTERS = new Set(['all', ...ORDER_STATUS_OPTIONS.map((option) => option.value)]);

export default function AdminOrders({ admin, logout, isSidebarCollapsed, toggleSidebar }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ show: false, text: '', variant: '' });
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [statusDrafts, setStatusDrafts] = useState({});
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [notifications] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [toasts, setToasts] = useState([]);
  const [showSlipModal, setShowSlipModal] = useState(false);
  const [selectedSlipUrl, setSelectedSlipUrl] = useState('');

  useEffect(() => {
    fetchOrders();
    // จำลองการแจ้งเตือน (ในอนาคตอาจจะใช้ WebSocket)
    const interval = setInterval(() => {
      // ตรวจสอบออเดอร์ใหม่หรือสถานะที่เปลี่ยน
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Sync filter จาก URL query params (เมื่อ component mount หรือ URL เปลี่ยนจากภายนอก)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const filterParam = params.get('filter');
    const newFilter = filterParam && ALLOWED_STATUS_FILTERS.has(filterParam) ? filterParam : 'all';
    setStatusFilter((currentFilter) => {
      // อัปเดตเฉพาะเมื่อค่าเปลี่ยนจริงๆ
      return newFilter !== currentFilter ? newFilter : currentFilter;
    });
  }, [location.search]);

  // Update URL เมื่อเปลี่ยน filter
  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    const params = new URLSearchParams(location.search);
    if (newFilter === 'all') {
      params.delete('filter');
    } else {
      params.set('filter', newFilter);
    }
    const newSearch = params.toString();
    navigate(`/admin/orders${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get('/api/orders');
      // เรียงลำดับออเดอร์ใหม่ที่สุดก่อน (ตามวันที่และ order_id)
      const sortedData = (response.data || []).sort((a, b) => {
        const dateDiff = new Date(b.order_date).getTime() - new Date(a.order_date).getTime();
        if (dateDiff !== 0) return dateDiff;
        return Number(b.order_id) - Number(a.order_id);
      });
      setOrders(sortedData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setIsLoading(false);
    }
  };

  const toggleOrderExpand = (orderId) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  const handleViewDetails = async (orderId) => {
    try {
      const response = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(response.data);
      setShowOrderModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'ไม่สามารถโหลดรายละเอียดคำสั่งซื้อได้';
      setMessage({ show: true, text: errorMessage, variant: 'danger' });
      setTimeout(() => setMessage({ show: false }), 3000);
    }
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const handleStatusDraftChange = (orderId, nextStatus) => {
    setStatusDrafts((prev) => ({
      ...prev,
      [orderId]: nextStatus
    }));
  };

  const showToast = (message, variant = 'success') => {
    const id = Date.now();
    const toast = { id, message, variant };
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  };

  const handleStatusUpdate = async (orderId) => {
    const targetOrder = orders.find((order) => order.order_id === orderId);
    if (!targetOrder) {
      return;
    }

    const nextStatus = statusDrafts[orderId] ?? targetOrder.order_status;
    if (!nextStatus || nextStatus === targetOrder.order_status) {
      showToast('สถานะคำสั่งซื้อไม่ได้เปลี่ยนแปลง', 'info');
      return;
    }

    setSavingStatusId(orderId);
    try {
      await api.put(`/api/orders/${orderId}/status`, { status: nextStatus });
      const statusLabel = ORDER_STATUS_OPTIONS.find((opt) => opt.value === nextStatus)?.label || nextStatus;
      showToast(`คำสั่งซื้อ #${orderId} เปลี่ยนสถานะเป็น "${statusLabel}" เรียบร้อยแล้ว`, 'success');
      setStatusDrafts((prev) => {
        const updated = { ...prev };
        delete updated[orderId];
        return updated;
      });
      await fetchOrders();
      if (selectedOrder && selectedOrder.order_id === orderId) {
        const updatedResponse = await api.get(`/api/orders/${orderId}`);
        setSelectedOrder(updatedResponse.data);
        setShowOrderModal(true);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      const errorMessage = error.response?.data?.error || 'ไม่สามารถอัปเดตสถานะคำสั่งซื้อได้';
      showToast(errorMessage, 'danger');
    } finally {
      setSavingStatusId(null);
    }
  };

  const handleApprove = async (orderId) => {
    if (!window.confirm('ยืนยันการอนุมัติคำสั่งซื้อนี้?')) {
      return;
    }

    try {
      await api.put(`/api/orders/approve/${orderId}`);
      showToast(`คำสั่งซื้อ #${orderId} อนุมัติเรียบร้อยแล้ว`, 'success');
      await fetchOrders();
    } catch (error) {
      console.error('Error approving order:', error);
      showToast('เกิดข้อผิดพลาดในการอนุมัติคำสั่งซื้อ', 'danger');
    }
  };

  const handleReject = async (orderId) => {
    if (!window.confirm('ต้องการไม่อนุมัติคำสั่งซื้อนี้หรือไม่?')) {
      return;
    }

    try {
      await api.put(`/api/orders/reject/${orderId}`);
      showToast(`คำสั่งซื้อ #${orderId} ถูกยกเลิกแล้ว`, 'warning');
      await fetchOrders();
    } catch (error) {
      console.error('Error rejecting order:', error);
      showToast('ไม่สามารถไม่อนุมัติคำสั่งซื้อได้', 'danger');
    }
  };

  const handleFixOrderStatuses = async () => {
    if (!window.confirm('ต้องการอัปเดตสถานะออเดอร์ที่จ่ายงวดแรกแล้วแต่สถานะยังเป็น "รอชำระเงิน" หรือไม่?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await api.post('/api/admin/orders/fix-status', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      showToast(response.data.message || 'อัปเดตสถานะออเดอร์เรียบร้อยแล้ว', 'success');
      await fetchOrders();
    } catch (error) {
      console.error('Error fixing order statuses:', error);
      showToast('ไม่สามารถอัปเดตสถานะออเดอร์ได้', 'danger');
    }
  };

  const formatCurrency = (value) => {
    if (value == null || value === '') {
      return '-';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
      return '-';
    }
    return `฿${number.toLocaleString('th-TH', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })}`;
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear() + 543; // Convert to Buddhist Era
    return `${day}/${month}/${year}`;
  };


  const formatShippingAddress = (shipping) => {
    if (!shipping) {
      return '-';
    }
    const parts = [];
    const fullName = `${shipping.recipientName || ''} ${shipping.recipientSurname || ''}`.trim();
    if (fullName) {
      parts.push(fullName);
    }
    if (shipping.phone) {
      parts.push(`โทร: ${shipping.phone}`);
    }
    if (shipping.address) {
      parts.push(shipping.address);
    }
    return parts.length > 0 ? parts.join('\n') : '-';
  };

  const getPaymentMethodLabel = (order) => {
    if (!order) {
      return '-';
    }
    const method = order.payment_method ? String(order.payment_method).toLowerCase() : '';
    if (method.includes('install')) {
      return 'ผ่อนชำระ';
    }
    if (method.includes('cash')) {
      return 'ชำระเต็มจำนวน';
    }
    if (method.includes('cod') || method.includes('delivery') || method.includes('ปลายทาง')) {
      return 'ชำระปลายทาง';
    }
    return order.installment_periods > 1 ? 'ผ่อนชำระ' : 'ชำระเต็มจำนวน';
  };

  const isInstallmentOrder = (order) => {
    if (!order) return false;
    const method = order.payment_method ? String(order.payment_method).toLowerCase() : '';
    if (method.includes('install')) {
      return true;
    }
    return Number(order.installment_periods) > 1;
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    
    return (
      <span 
        className="order-status-badge" 
        style={{ 
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: '500'
        }}
      >
        {config.label}
      </span>
    );
  };

  const statusFilterOptions = useMemo(
    () => ({
      all: { label: 'ทั้งหมด', predicate: () => true },
      pending: { label: 'รออนุมัติ', predicate: (order) => order.order_status === 'pending' },
      awaiting_payment: {
        label: 'รอชำระเงิน',
        predicate: (order) => order.order_status === 'awaiting_payment'
      },
      waiting_for_delivery: {
        label: 'กำลังจัดส่ง',
        predicate: (order) => order.order_status === 'waiting_for_delivery'
      },
      completed: {
        label: 'จัดส่งสำเร็จ',
        predicate: (order) => order.order_status === 'completed'
      },
      cancelled: {
        label: 'ยกเลิกโดยผู้ดูแล',
        predicate: (order) => order.order_status === 'cancelled'
      },
      cancelled_by_customer: {
        label: 'ลูกค้ายกเลิก',
        predicate: (order) => order.order_status === 'cancelled_by_customer'
      }
    }),
    []
  );

  const statusCounts = useMemo(() => {
    const baseCounts = Object.fromEntries(Object.keys(statusFilterOptions).map((key) => [key, 0]));
    baseCounts.all = orders.length;

    orders.forEach((order) => {
      Object.entries(statusFilterOptions).forEach(([key, option]) => {
        if (key === 'all') {
          return;
        }
        if (option.predicate(order)) {
          baseCounts[key] += 1;
        }
      });
    });

    return baseCounts;
  }, [orders, statusFilterOptions]);

  const totalRevenue = useMemo(
    () => orders.reduce((sum, order) => sum + Number(order.total_amount || 0), 0),
    [orders]
  );

  const filteredOrders = useMemo(() => {
    let result = orders;

    // Filter by search
    const cleanedSearch = orderSearch.replace(/#/g, '').trim().toLowerCase();
    if (cleanedSearch) {
      result = result.filter((order) => {
        const fields = [
          String(order.order_id),
          order.customer_fname,
          order.customer_lname,
          order.customer_email,
          order.customer_tel
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return fields.includes(cleanedSearch);
      });
    }

    // Filter by status
    const filter = statusFilterOptions[statusFilter];
    if (filter) {
      result = result.filter(filter.predicate);
    }

    // Filter by date range
    if (filterDateFrom || filterDateTo) {
      result = result.filter((order) => {
        const orderDate = new Date(order.order_date);
        if (filterDateFrom && orderDate < new Date(filterDateFrom)) {
          return false;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (orderDate > toDate) {
            return false;
          }
        }
        return true;
      });
    }

    return result;
  }, [orders, orderSearch, statusFilter, statusFilterOptions, filterDateFrom, filterDateTo]);

  const sortedOrders = useMemo(() => {
    const orderList = [...filteredOrders];

    switch (sortBy) {
      case 'oldest':
        return orderList.sort((a, b) => {
          const dateDiff = new Date(a.order_date).getTime() - new Date(b.order_date).getTime();
          if (dateDiff !== 0) return dateDiff;
          return Number(a.order_id) - Number(b.order_id);
        });
      case 'amount_desc':
        return orderList.sort((a, b) => Number(b.total_amount) - Number(a.total_amount));
      case 'amount_asc':
        return orderList.sort((a, b) => Number(a.total_amount) - Number(b.total_amount));
      case 'newest':
      default:
        // เรียงตามวันที่ใหม่ที่สุดก่อน และถ้าวันที่เท่ากันให้เรียงตาม order_id ใหม่ที่สุดก่อน
        return orderList.sort((a, b) => {
          const dateDiff = new Date(b.order_date).getTime() - new Date(a.order_date).getTime();
          if (dateDiff !== 0) return dateDiff;
          return Number(b.order_id) - Number(a.order_id);
        });
    }
  }, [filteredOrders, sortBy]);

  const handleClearFilters = () => {
    handleStatusFilterChange('all');
    setOrderSearch('');
    setSortBy('newest');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const statusSummary = useMemo(
    () => [
      { key: 'all', label: 'ทั้งหมด', count: statusCounts.all || 0, color: '#6c757d' },
      { key: 'pending', label: 'รออนุมัติ', count: statusCounts.pending || 0, color: getStatusConfig('pending').color },
      { key: 'awaiting_payment', label: 'รอชำระเงิน', count: statusCounts.awaiting_payment || 0, color: getStatusConfig('awaiting_payment').color },
      { key: 'waiting_for_delivery', label: 'กำลังจัดส่ง', count: statusCounts.waiting_for_delivery || 0, color: getStatusConfig('waiting_for_delivery').color },
      { key: 'completed', label: 'ส่งสำเร็จ', count: statusCounts.completed || 0, color: getStatusConfig('completed').color },
      { key: 'cancelled', label: 'ยกเลิกโดยผู้ดูแล', count: statusCounts.cancelled || 0, color: getStatusConfig('cancelled').color },
      { key: 'cancelled_by_customer', label: 'ลูกค้ายกเลิก', count: statusCounts.cancelled_by_customer || 0, color: getStatusConfig('cancelled_by_customer').color }
    ],
    [statusCounts]
  );

  if (isLoading) {
    return (
      <div className="admin-orders-page">
        <AdminHeader
          admin={admin}
          onLogout={logout}
          isCollapsed={isSidebarCollapsed}
          onToggleSidebar={toggleSidebar}
        />
        <div className="admin-orders__loading">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted mt-3">กำลังดึงข้อมูลคำสั่งซื้อ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-orders-page">
      <AdminHeader
        admin={admin}
        onLogout={logout}
        isCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />

      {/* Notification Corner */}
      <div className="admin-orders__notifications">
        <button className="admin-orders__notification-btn" title="แจ้งเตือน">
          <i className="bi bi-bell"></i>
          {notifications.length > 0 && (
            <span className="admin-orders__notification-badge">{notifications.length}</span>
          )}
        </button>
      </div>

      <div className="admin-orders__layout">
        <Container fluid className="admin-orders__container">
          {/* Header Bar */}
          <section className="admin-orders__header-bar">
            <div className="d-flex justify-content-end mb-3">
              <Button 
                variant="outline-warning" 
                size="sm"
                onClick={handleFixOrderStatuses}
                title="อัปเดตสถานะออเดอร์ที่จ่ายงวดแรกแล้ว"
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                แก้ไขสถานะออเดอร์ที่จ่ายงวดแรกแล้ว
              </Button>
            </div>
            <div className="admin-orders__search-wrapper">
              <i className="bi bi-search"></i>
              <input
                type="text"
                className="admin-orders__search-input"
                placeholder="ค้นหาเลขคำสั่งซื้อ ชื่อลูกค้า หรือเบอร์โทร"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
              />
            </div>
            <div className="admin-orders__header-actions">
              <div className="admin-orders__sort-wrapper">
                <i className="bi bi-sort-down"></i>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="admin-orders__sort-select"
                >
                  <option value="newest">ใหม่ล่าสุด</option>
                  <option value="oldest">เก่าสุด</option>
                  <option value="amount_desc">ยอดรวมสูงสุด</option>
                  <option value="amount_asc">ยอดรวมต่ำสุด</option>
                </Form.Select>
              </div>
              <Button
                variant="outline-secondary"
                className="admin-orders__filter-btn"
                onClick={() => setShowFilterModal(true)}
              >
                <i className="bi bi-funnel"></i>
                ตัวกรอง
              </Button>
              <Button
                variant="primary"
                className="admin-orders__refresh-btn"
                onClick={fetchOrders}
              >
                <i className="bi bi-arrow-repeat"></i>
                รีเฟรชข้อมูล
              </Button>
            </div>
          </section>

          {message.show && (
            <Alert
              variant={message.variant}
              dismissible
              onClose={() => setMessage({ show: false })}
              className="admin-orders-alert"
            >
              {message.text}
            </Alert>
          )}

          {/* Status Summary Bar - Filter Tabs */}
          <section className="admin-orders__status-summary">
            {statusSummary.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`admin-orders__status-card ${statusFilter === item.key ? 'is-active' : ''}`}
                onClick={() => handleStatusFilterChange(item.key)}
                style={{ '--status-color': item.color }}
              >
                <span className="admin-orders__status-label">{item.label}</span>
                <strong className="admin-orders__status-count">{item.count}</strong>
              </button>
            ))}
            <div className="admin-orders__status-total">
              <span>ยอดขายรวม</span>
              <strong>{formatCurrency(totalRevenue)}</strong>
            </div>
          </section>

          {/* Orders List - Card Table Hybrid */}
          <section className="admin-orders__list">
            {sortedOrders.length === 0 ? (
              <div className="admin-orders-empty-state">
                <div className="admin-orders-empty-state__icon">
                  <i className="bi bi-inbox"></i>
                </div>
                <h4>ไม่พบคำสั่งซื้อที่ตรงกับเงื่อนไข</h4>
                <p className="text-muted">ลองปรับตัวกรองหรือค้นหาด้วยคำอื่นๆ อีกครั้ง</p>
                <Button variant="primary" onClick={handleClearFilters}>
                  ล้างตัวกรองทั้งหมด
                </Button>
              </div>
            ) : (
              <div className="admin-orders__cards">
                {sortedOrders.map((order) => {
                  const currentStatusValue = statusDrafts[order.order_id] ?? order.order_status;
                  const statusChanged = currentStatusValue !== order.order_status;
                  const isExpanded = expandedOrders.has(order.order_id);
                  const statusConfig = getStatusConfig(order.order_status);
                  
                  return (
                    <div 
                      key={order.order_id} 
                      className="admin-order-card admin-order-card--compact"
                      data-status={order.order_status}
                      style={{ '--status-color': statusConfig.color }}
                    >
                      {/* Compact Row - Main Info */}
                      <div className="admin-order-card__row">
                        <div className="admin-order-card__id-col">
                          <strong className="admin-order-card__id">#{order.order_id}</strong>
                        </div>
                        <div className="admin-order-card__customer-col">
                          <div className="admin-order-card__customer-name">
                            {order.customer_fname} {order.customer_lname}
                          </div>
                          <div className="admin-order-card__customer-contact">
                            <span title="เบอร์โทร">
                              <i className="bi bi-telephone"></i>
                              {order.customer_tel || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="admin-order-card__date-col">
                          {formatDate(order.order_date)}
                        </div>
                        <div className="admin-order-card__amount-col">
                          <strong>{formatCurrency(order.total_amount)}</strong>
                        </div>
                        <div className="admin-order-card__installment-col">
                          {Number(order.installment_periods) > 0 ? `${order.installment_periods} งวด` : '-'}
                        </div>
                        <div className="admin-order-card__status-col">
                          {getStatusBadge(order.order_status)}
                        </div>
                        <div className="admin-order-card__actions-col">
                          <div className="admin-order-card__quick-actions">
                            {order.order_status === 'pending' && (
                              <>
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleApprove(order.order_id)}
                                  title="อนุมัติ"
                                >
                                  <i className="bi bi-check-circle"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleReject(order.order_id)}
                                  title="ไม่อนุมัติ"
                                >
                                  <i className="bi bi-x-circle"></i>
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="admin-order-card__view-btn"
                              onClick={() => handleViewDetails(order.order_id)}
                              title="ดูรายละเอียด"
                            >
                              <i className="bi bi-eye"></i>
                            </Button>
                            <button
                              className="admin-order-card__expand-btn"
                              onClick={() => toggleOrderExpand(order.order_id)}
                              title={isExpanded ? 'ย่อรายละเอียด' : 'ขยายรายละเอียด'}
                            >
                              <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Details */}
                      {isExpanded && (
                        <div className="admin-order-card__expandable">
                          <div className="admin-order-card__expandable-content">
                            <div className="admin-order-card__expandable-section">
                              <div className="admin-order-card__expandable-row">
                                <span className="admin-order-card__expandable-label">อีเมล:</span>
                                <span>{order.customer_email || '-'}</span>
                              </div>
                            </div>
                            <div className="admin-order-card__expandable-section">
                              <div className="admin-order-card__status-editor-compact">
                                <div className="admin-order-card__status-change-compact">
                                  <span className="admin-order-card__status-label-compact">เปลี่ยนสถานะ:</span>
                                  <div className="admin-order-card__status-select-wrapper-compact">
                                    <Form.Select
                                      size="sm"
                                      value={currentStatusValue}
                                      onChange={(e) =>
                                        handleStatusDraftChange(order.order_id, e.target.value)
                                      }
                                      className="admin-order-card__status-select-compact"
                                    >
                                      {ORDER_STATUS_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </div>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="admin-order-card__status-save-compact"
                                    disabled={!statusChanged || savingStatusId === order.order_id}
                                    onClick={() => handleStatusUpdate(order.order_id)}
                                  >
                                    {savingStatusId === order.order_id ? (
                                      <Spinner animation="border" size="sm" role="status">
                                        <span className="visually-hidden">Saving...</span>
                                      </Spinner>
                                    ) : (
                                      <>
                                        <i className="bi bi-save me-1"></i>
                                        บันทึก
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </Container>
      </div>

      {/* Order Details Modal */}
      <Modal
        show={showOrderModal}
        onHide={handleCloseOrderModal}
        size="lg"
        centered
        scrollable
        contentClassName="admin-orders-order-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-receipt me-2"></i>
            รายละเอียดคำสั่งซื้อ {selectedOrder ? `#${selectedOrder.order_id}` : ''}
          </Modal.Title>
        </Modal.Header>
        {selectedOrder && (
          <Modal.Body>
            <div className="admin-order-detail-section">
              <h4>
                <i className="bi bi-person"></i>
                ข้อมูลลูกค้า
              </h4>
              <div className="admin-order-detail-content">
                <div className="admin-order-detail-row">
                  <span>ชื่อ:</span>
                  <strong>
                    {selectedOrder.customer_fname} {selectedOrder.customer_lname}
                  </strong>
                </div>
                <div className="admin-order-detail-row">
                  <span>เบอร์:</span>
                  <strong>{selectedOrder.customer_tel || '-'}</strong>
                </div>
                <div className="admin-order-detail-row">
                  <span>อีเมล:</span>
                  <strong>{selectedOrder.customer_email || '-'}</strong>
                </div>
                <div className="admin-order-detail-row">
                  <span>ที่อยู่จัดส่ง:</span>
                  <strong style={{ whiteSpace: 'pre-line', lineHeight: '1.6' }}>
                    {formatShippingAddress(selectedOrder.shipping_address || selectedOrder.primaryShipping)}
                  </strong>
                </div>
              </div>
            </div>

            <div className="admin-order-detail-section">
              <h4>
                <i className="bi bi-box-seam"></i>
                รายการสินค้า
              </h4>
              <div className="admin-order-detail-content">
                {selectedOrder.details && selectedOrder.details.length > 0 ? (
                  <div className="admin-order-detail-items">
                    {selectedOrder.details.map((item) => (
                      <div key={item.order_detail_id} className="admin-order-detail-item">
                        <div className="admin-order-detail-item__name">{item.product_name}</div>
                        <div className="admin-order-detail-item__meta">
                          <span>x{item.quantity}</span>
                          <strong>{formatCurrency(item.price)}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">ไม่มีรายการสินค้า</p>
                )}
              </div>
            </div>

            <div className="admin-order-detail-section">
              <h4>
                <i className="bi bi-currency-dollar"></i>
                สรุปยอด
              </h4>
              <div className="admin-order-detail-content">
                <div className="admin-order-detail-row">
                  <span>ยอดรวม:</span>
                  <strong className="admin-order-detail-total">
                    {formatCurrency(selectedOrder.total_amount)}
                  </strong>
                </div>
                <div className="admin-order-detail-row">
                  <span>วิธีชำระ:</span>
                  <strong>{getPaymentMethodLabel(selectedOrder)}</strong>
                </div>
                {isInstallmentOrder(selectedOrder) && (
                  <div className="admin-order-detail-row">
                    <span>จำนวนงวด:</span>
                    <strong>{selectedOrder.installment_periods} งวด</strong>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-order-detail-section">
              <h4>
                <i className="bi bi-info-circle"></i>
                สถานะปัจจุบัน
              </h4>
              <div className="admin-order-detail-content">
                <div className="admin-order-detail-status">
                  {getStatusBadge(selectedOrder.order_status)}
                </div>
                <div className="admin-order-detail-status-change">
                  <span className="admin-order-detail-status-label">เปลี่ยนเป็น</span>
                  <div className="admin-order-detail-status-select-wrapper">
                    <i className="bi bi-arrow-repeat"></i>
                    <Form.Select
                      value={statusDrafts[selectedOrder.order_id] ?? selectedOrder.order_status}
                      onChange={(e) =>
                        handleStatusDraftChange(selectedOrder.order_id, e.target.value)
                      }
                      className="admin-order-detail-status-select"
                    >
                      {ORDER_STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className={`admin-order-detail-status-save ${
                      (!statusDrafts[selectedOrder.order_id] ||
                      statusDrafts[selectedOrder.order_id] === selectedOrder.order_status ||
                      savingStatusId === selectedOrder.order_id)
                        ? 'admin-order-detail-status-save--disabled'
                        : 'admin-order-detail-status-save--enabled'
                    }`}
                    disabled={
                      !statusDrafts[selectedOrder.order_id] ||
                      statusDrafts[selectedOrder.order_id] === selectedOrder.order_status ||
                      savingStatusId === selectedOrder.order_id
                    }
                    onClick={() => handleStatusUpdate(selectedOrder.order_id)}
                  >
                    {savingStatusId === selectedOrder.order_id ? (
                      <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Saving...</span>
                      </Spinner>
                    ) : (
                      <>
                        <i className="bi bi-save me-1"></i>
                        บันทึก
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {isInstallmentOrder(selectedOrder) && selectedOrder.installments && (
              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-calendar-check"></i>
                  ตารางผ่อนชำระ
                </h4>
                <div className="admin-order-detail-content">
                  {selectedOrder.installments.length > 0 ? (
                    <div className="admin-order-detail-installments">
                      {selectedOrder.installments.map((installment) => (
                        <div key={installment.installment_id} className="admin-order-detail-installment">
                          <div className="admin-order-detail-installment__number">
                            งวด {installment.installment_number}
                          </div>
                          <div className="admin-order-detail-installment__amount">
                            {formatCurrency(installment.installment_amount)}
                          </div>
                          <div className="admin-order-detail-installment__date">
                            กำหนดชำระ: {formatDate(installment.payment_due_date)}
                          </div>
                          <div className="admin-order-detail-installment__status">
                            <Badge
                              bg={installment.payment_status === 'paid' ? 'success' : 'secondary'}
                            >
                              {installment.payment_status === 'paid' ? 'ชำระแล้ว' : 'รอชำระ'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">ยังไม่มีข้อมูลตารางผ่อนชำระ</p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Slips Section */}
            {selectedOrder.payments && selectedOrder.payments.length > 0 && (
              <div className="admin-order-detail-section">
                <h4>
                  <i className="bi bi-receipt"></i>
                  สลิปการชำระเงิน
                </h4>
                <div className="admin-order-detail-content">
                  <div className="admin-order-detail-payments">
                    {selectedOrder.payments.map((payment) => (
                      <div key={payment.payment_id} className="admin-order-detail-payment">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <div>
                            <strong>
                              {payment.installment_number 
                                ? `งวดที่ ${payment.installment_number}` 
                                : 'ชำระเต็มจำนวน'}
                            </strong>
                            <span className="ms-2">{formatCurrency(payment.amount)}</span>
                          </div>
                          <Badge 
                            bg={
                              payment.status === 'approved' ? 'success' : 
                              payment.status === 'rejected' ? 'danger' : 
                              'warning'
                            }
                          >
                            {payment.status === 'approved' ? 'อนุมัติแล้ว' : 
                             payment.status === 'rejected' ? 'ปฏิเสธแล้ว' : 
                             'รออนุมัติ'}
                          </Badge>
                        </div>
                        {payment.note && (
                          <div className="text-muted small mb-2">
                            <strong>หมายเหตุ:</strong> {payment.note}
                          </div>
                        )}
                        {payment.rejection_reason && (
                          <div className="text-danger small mb-2">
                            <strong>เหตุผลปฏิเสธ:</strong> {payment.rejection_reason}
                          </div>
                        )}
                        {payment.slip_url && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSelectedSlipUrl(payment.slip_url);
                              setShowSlipModal(true);
                            }}
                          >
                            <i className="bi bi-image me-1"></i>
                            ดูสลิป
                          </Button>
                        )}
                        <div className="text-muted small mt-2">
                          วันที่แจ้ง: {formatDate(payment.created_at)}
                          {payment.approved_at && (
                            <span className="ms-2">
                              วันที่อนุมัติ: {formatDate(payment.approved_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="admin-order-detail-actions">
              {selectedOrder.order_status !== 'cancelled' &&
                selectedOrder.order_status !== 'cancelled_by_customer' && (
                  <Button variant="outline-danger" size="sm" onClick={() => handleReject(selectedOrder.order_id)}>
                    <i className="bi bi-x-circle me-1"></i>
                    ยกเลิก
                  </Button>
                )}
            </div>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseOrderModal}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Slip Image Modal */}
      <Modal show={showSlipModal} onHide={() => setShowSlipModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>สลิปโอนเงิน</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedSlipUrl && (
            <img
              src={selectedSlipUrl}
              alt="Payment Slip"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSlipModal(false)}>
            ปิด
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast Notifications */}
      <div className="admin-orders__toast-container">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`admin-orders__toast admin-orders__toast--${toast.variant}`}
          >
            <div className="admin-orders__toast-icon">
              {toast.variant === 'success' && <i className="bi bi-check-circle-fill"></i>}
              {toast.variant === 'danger' && <i className="bi bi-x-circle-fill"></i>}
              {toast.variant === 'warning' && <i className="bi bi-exclamation-triangle-fill"></i>}
              {toast.variant === 'info' && <i className="bi bi-info-circle-fill"></i>}
            </div>
            <div className="admin-orders__toast-message">{toast.message}</div>
          </div>
        ))}
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ตัวกรองคำสั่งซื้อ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="admin-orders-filter-form">
            <div className="admin-orders-filter-group">
              <label>ช่วงวันที่</label>
              <div className="admin-orders-filter-dates">
                <Form.Control
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  placeholder="จากวันที่"
                />
                <span>ถึง</span>
                <Form.Control
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  placeholder="ถึงวันที่"
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleClearFilters}>
            ล้างตัวกรอง
          </Button>
          <Button variant="primary" onClick={() => setShowFilterModal(false)}>
            ตกลง
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
